import React, { useState, useEffect, useCallback, memo } from 'react';
import { Alert, Spin } from 'antd';
import classNames from 'classnames';
import PageMeta from 'components/PageMeta/PageMeta';
import StepsLayout from 'components/StepsLayout';
import useBreadcrumbsParams from 'hooks/useBreadcrumbsParams';
import { useHistory, useParams } from 'react-router-dom';
import { ADD_APPLICATION_STEPS, ADD_AIR_FORCE_HCC_STEPS, ADD_AIR_FORCE_HCC_STEPS_MAPPING } from 'constants/index';
import { SnapshotContext, sleep } from 'utils';
import { selectors, actions as applicationStateActions } from 'features/application-state';
import { selectors as centerSelectors, actions as centerActions } from 'features/add-center';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser } from 'features/auth';
import invert from 'lodash/invert';
import AirForceHCCCredentials from './StepComponents/Credentials/AirForceHCCCredentials';
import AirForceHCCInsurance from './StepComponents/Insurance/AirForceHCCInsurance';
import AgreementStep from '../__shared/AgreementStep';
import HCCHoursOfOperation from '../__shared/HoursOfOperation/HCCHoursOfOperation';
import HCCResidents from '../__shared/Residents/HCCResidents';
import HCCStaffMembers from '../__shared/StaffMembers/HCCStaffMembers';
import HCCProgramInfo from '../__shared/ProgramInfo/HCCProgramInfo';
import ApplicationSubmissionSuccess from '../__shared/SubmissionSuccess';
import { isApplicationSubmitted } from '../ApplicationProgramWrapper';

const steps = [
  { title: 'Program info' },
  { title: 'Credentials' },
  { title: 'Hours of operation' },
  { title: 'Staff members' },
  { title: 'Residents' },
  { title: 'Insurance' },
  { title: 'Agreement' },
];

const AirForceHCCApplication = ({ application, setApplication }) => {
  useBreadcrumbsParams('id');
  const history = useHistory();
  const dispatch = useDispatch();
  const [loading] = useState(false);
  const userInfo = useSelector(selectUser);
  let { id, step = ADD_AIR_FORCE_HCC_STEPS.PROGRAM_INFO } = useParams();
  const stepNumber = ADD_AIR_FORCE_HCC_STEPS_MAPPING[step];
  const [applicationSubmitted, setApplicationSubmitted] = useState(isApplicationSubmitted(application));
  const openSteps = useSelector(selectors.selectProviderOnboardingOpenSteps);
  const isFormSubmitting = useSelector(centerSelectors.selectIsFormSubmitting);

  const setOpenSteps = useCallback(
    (step) => {
      dispatch(applicationStateActions.setProviderOnboardingOpenSteps({ [step]: true }));
    },
    [dispatch],
  );

  const setStep = useCallback(
    async (newStep) => {
      setOpenSteps(newStep);
      await sleep(100);
      history.push(`/provider/applications/${id}/${newStep}`);
    },
    [history, id, setOpenSteps],
  );

  const onCancel = useCallback(() => {
    history.push('/provider');
  }, [history]);

  useEffect(() => {
    /* istanbul ignore else */
    if (id && Number.isNaN(Number(id))) {
      history.push('/provider');
    }
  }, [history, id]);

  useEffect(() => {
    /* istanbul ignore else */
    if (step) {
      setTimeout(() => {
        const container = document.getElementById('page-top-placeholder');
        container?.scrollIntoView(
          {
            behavior: 'smooth',
          },
          300,
        );
      }, 500);
    }
  }, [step]);

  useEffect(() => {
    if (isApplicationSubmitted(application)) {
      setApplicationSubmitted(true);
    }
  }, [application, setApplicationSubmitted]);

  const commonProps = {
    loading,
    id,
    userInfo,
    step,
    setStep,
    onCancel,
    application,
    setApplication,
    isFormSubmitting,
    actions: centerActions,
    isSubmitted: applicationSubmitted,
    setSubmitted: setApplicationSubmitted,
    applicationSubmitted,
  };

  return (
    <div id="add-center-container" className={classNames('layout-page')}>
      <PageMeta title="Air Force Home Community Care Application" />

      <div id="page-top-placeholder"></div>

      {step !== ADD_APPLICATION_STEPS.SUBMITTED && (
        <StepsLayout
          steps={steps}
          step={stepNumber}
          setStep={(number) => {
            const step = invert(ADD_AIR_FORCE_HCC_STEPS_MAPPING)[number];
            setStep(step);
          }}
          openSteps={openSteps}
          isSubmitted={applicationSubmitted}
        />
      )}

      {applicationSubmitted && step !== ADD_APPLICATION_STEPS.SUBMITTED && (
        <div className="px-7 pb-7">
          <Alert
            message={<p className="font-semibold">Application is no longer editable</p>}
            description="Application has been submitted, and you can no longer make changes."
            type="info"
            showIcon
          />
        </div>
      )}

      <SnapshotContext.Provider value={{ snapshotSelector: centerSelectors.selectCenterSnapshot }}>
        <Spin size="large" spinning={loading}>
          <div className="px-7 pb-7">
            {step === ADD_AIR_FORCE_HCC_STEPS.PROGRAM_INFO && <HCCProgramInfo {...commonProps} />}
            {step === ADD_AIR_FORCE_HCC_STEPS.CREDENTIALS && <AirForceHCCCredentials {...commonProps} />}
            {step === ADD_AIR_FORCE_HCC_STEPS.HOURS_OF_OPERATION && <HCCHoursOfOperation {...commonProps} />}
            {step === ADD_AIR_FORCE_HCC_STEPS.STAFF_MEMBERS && <HCCStaffMembers {...commonProps} />}
            {step === ADD_AIR_FORCE_HCC_STEPS.RESIDENTS && <HCCResidents {...commonProps} />}
            {step === ADD_AIR_FORCE_HCC_STEPS.INSURANCE && <AirForceHCCInsurance {...commonProps} />}
            {step === ADD_AIR_FORCE_HCC_STEPS.AGREEMENT && (
              <AgreementStep {...commonProps} previousStep={ADD_AIR_FORCE_HCC_STEPS.INSURANCE} />
            )}
            {step === ADD_APPLICATION_STEPS.SUBMITTED && <ApplicationSubmissionSuccess {...commonProps} />}
          </div>
        </Spin>
      </SnapshotContext.Provider>
    </div>
  );
};

export default memo(AirForceHCCApplication);
